import { Component, inject, OnInit } from '@angular/core';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { NgForOf, NgIf } from '@angular/common';
import type { KeycloakProfile, KeycloakTokenParsed } from 'keycloak-js';
import Keycloak from 'keycloak-js';
import { Divider } from 'primeng/divider';

export interface UserProfileMetadataAttribute {
    name: string;
    displayName: string;
    required: boolean;
    readOnly: boolean;
    multivalued: boolean;
    validators?: Record<string, any>;
    annotations?: Record<string, any>;
    group?: string;
}

export interface UserProfileMetadataGroup {
    name: string;
    displayHeader: string;
    displayDescription: string;
}

export interface UserProfileMetadata {
    attributes: UserProfileMetadataAttribute[];
    groups?: UserProfileMetadataGroup[];
}

// agora estenda KeycloakProfile:
export interface KeycloakProfileWithMetadata extends KeycloakProfile {
    userProfileMetadata?: UserProfileMetadata;
}


@Component({
    selector: 'app-userinfo-dialog',
    standalone: true,
    imports: [
        DynamicDialogModule,
        NgIf,
        NgForOf,          // <-- adicionado para usar *ngFor no template
        TableModule,
        ButtonModule,
        Divider
    ],
    template: `
        <div class="m-2" *ngIf="profile">
            <table style="width:100%;">
                <tr><td><strong>ID:</strong></td><td>{{ profile.id }}</td></tr>
                <tr><td><strong>Nome:</strong></td><td>{{ profile.firstName }} {{ profile.lastName }}</td></tr>
                <tr><td><strong>Username:</strong></td><td>{{ profile.username }}</td></tr>
                <tr><td><strong>Email:</strong></td><td>{{ profile.email }}</td></tr>
                <!-- aqui incluímos os atributos personalizados -->
                <ng-container *ngIf="userProfileMetadata">
                    <tr *ngFor="let meta of filledAttributes">
                        <td><strong>{{ meta.displayName }}:</strong></td>
                        <td>{{ attributeMap[meta.name] }}</td>
                    </tr>
                </ng-container>
            </table>
        </div>

        <p-divider align="center">
            <b>Papéis na Organização</b>
        </p-divider>

        <div *ngIf="realmRolesData.length">
            <p-table [value]="realmRolesData" [paginator]="false">
                <ng-template pTemplate="header">
                    <tr><th>Tipo</th><th>Papéis</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr><td>{{ row.tipo }}</td><td>{{ row.roles }}</td></tr>
                </ng-template>
            </p-table>
        </div>

        <p-divider align="center">
            <b>Recursos</b>
        </p-divider>

        <div *ngIf="resourceRolesData.length">
            <p-table [value]="resourceRolesData" [paginator]="false">
                <ng-template pTemplate="header">
                    <tr><th>Recurso</th><th>Papéis</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr><td>{{ row.client }}</td><td>{{ row.roles }}</td></tr>
                </ng-template>
            </p-table>
        </div>
    `
})
export class UserInfoDialogComponent implements OnInit {
    profile: KeycloakProfileWithMetadata | null = null;
    tokenParsed: KeycloakTokenParsed | null = null;
    userProfileMetadata: UserProfileMetadata | undefined;
    realmRolesData: { tipo: string; roles: string }[] = [];
    resourceRolesData: { client: string; roles: string }[] = [];
    attributeMap: Record<string,string> = {};

    private kc = inject(Keycloak);

    get filledAttributes(): UserProfileMetadataAttribute[] {
        // Se não tiver metadata, devolve lista vazia
        if (!this.userProfileMetadata) {
            return [];
        }

        // Aqui TS sabe que attributes é UserProfileMetadataAttribute[]
        return this.userProfileMetadata.attributes
            .filter(meta => Boolean(this.attributeMap[meta.name]));
    }

    async ngOnInit(): Promise<void> {
        try {
            // 1. Carrega perfil
            this.profile = await this.kc.loadUserProfile();

            // 2. Token parseado
            const parsed = this.kc.tokenParsed as any;
            this.tokenParsed = parsed;

            // 3. Captura o metadata de atributos, se existir
            this.userProfileMetadata = this.profile.userProfileMetadata;

            // monta o map para o template
            if (this.profile.attributes && this.userProfileMetadata) {
                for (const meta of this.userProfileMetadata?.attributes) {
                    const raw = this.profile.attributes[meta.name];
                    if(raw){
                        this.attributeMap[meta.name] = Array.isArray(raw) && raw.length ? String(raw[0]) : '-';
                    }
                }

            }

            // 4. Realm Roles
            if (parsed.realm_access?.roles) {
                this.realmRolesData = [{
                    tipo: 'Realm Roles',
                    roles: parsed.realm_access.roles.join(', ')
                }];
            }

            // 5. Resource Roles
            const resources = parsed.resource_access || {};
            this.resourceRolesData = Object.keys(resources).map(client => ({
                client,
                roles: resources[client].roles.join(', ')
            }));
        } catch (err) {
            console.error('Erro ao carregar dados do Keycloak', err);
        }
    }
}
